/**
 * BaseSportScoring
 *
 * Abstract base class every sport-specific scorer extends.
 * Consumers should call getSportScoring(sportName) from index.js instead of
 * instantiating these directly.
 */
import { getMetricsForSport, isTappableMetric } from '../../../../config/sportsMetrics';

/** Categories with only the metrics that can be tapped (no %, no ratings — AS-B08). */
const tappableCategories = (categories) => {
  if (!categories) return categories;
  const out = {};
  for (const [catKey, cat] of Object.entries(categories)) {
    const metrics = Object.fromEntries(
      Object.entries(cat.metrics || {}).filter(([, m]) => isTappableMetric(m))
    );
    if (Object.keys(metrics).length > 0) out[catKey] = { ...cat, metrics };
  }
  return out;
};

class BaseSportScoring {
  /**
   * @param {string} sportKey  — lowercase, underscore-separated key matching sportsMetrics.js
   */
  constructor(sportKey) {
    this.sportKey = sportKey;
    this._config = getMetricsForSport(sportKey);
  }

  /** Human-readable sport label. Override in subclass if needed. */
  get label() {
    return this.sportKey;
  }

  /** All positions for this sport */
  get positions() {
    return this._config?.positions ?? [];
  }

  /**
   * Return all metric categories (full, unfiltered).
   * @returns {Object|null}  categories map
   */
  getCategories() {
    return tappableCategories(this._config?.categories ?? null);
  }

  /**
   * Return a flat ordered list of { key, metric } objects for the quick-tap
   * grid shown during a live match.  Falls back to every metric across all
   * categories when no quickMetrics list is configured. Legacy metrics are
   * never offered.
   *
   * @param {string} [position]   optional player position for filtering
   * @returns {Array<{key: string, metric: Object}>}
   */
  getQuickMetrics(position) {
    const config = this._config;
    if (!config) return [];

    // Build a flat map of ALL metrics across all categories
    const allMetrics = {};
    const cats = tappableCategories(
      position ? (getMetricsForSport(this.sportKey, position)?.categories ?? config.categories) : config.categories
    );
    for (const cat of Object.values(cats || {})) {
      for (const [k, m] of Object.entries(cat.metrics || {})) {
        if (!m.legacy) allMetrics[k] = m;
      }
    }

    if (config.quickMetrics && config.quickMetrics.length > 0) {
      // Return only the configured quick-metric keys (skip any that don't exist or are filtered out)
      return config.quickMetrics
        .filter((k) => k in allMetrics)
        .map((k) => ({ key: k, metric: allMetrics[k] }));
    }

    // Fallback: every metric in definition order
    return Object.entries(allMetrics).map(([key, metric]) => ({ key, metric }));
  }

  /**
   * Return categories filtered to only metrics valid for the given player position.
   * @param {string} [position]
   * @returns {Object|null}
   */
  getCategoriesForPosition(position) {
    if (!position) return this.getCategories();
    const filtered = getMetricsForSport(this.sportKey, position);
    return tappableCategories(filtered?.categories ?? null);
  }

  /**
   * Return a flat list of all score-affecting metric keys.
   * @returns {string[]}
   */
  getScoreMetricKeys() {
    const cats = this.getCategories();
    if (!cats) return [];
    const keys = [];
    for (const cat of Object.values(cats)) {
      for (const [key, m] of Object.entries(cat.metrics)) {
        if (m.affectsScore) keys.push(key);
      }
    }
    return keys;
  }

  /**
   * Build a standardised event payload for the event feed.
   *
   * @param {Object} metric   — metric definition from sportsMetrics.js
   * @param {string} metricKey — the camelCase key
   * @param {Object} player   — { id, name, position, jerseyNumber }
   * @param {string} participant — 'participant1' | 'participant2'
   * @returns {Object}
   */
  formatScoringEvent(metric, metricKey, player, participant) {
    return {
      type: 'advanced_score',
      participant,
      metricKey,
      metricLabel: metric.label,
      affectsScore: metric.affectsScore,
      isPositive: metric.isPositive,
      player: {
        id: player?.id,
        name: player?.name || player?.full_name || player?.username || 'Unknown',
        position: player?.position,
        jerseyNumber: player?.jersey_number ?? player?.jerseyNumber ?? null,
      },
      timestamp: Date.now(),
    };
  }

  /**
   * Derive a short event-feed description string.
   * @param {Object} metric   metric definition
   * @param {Object} player   player object
   * @returns {string}
   */
  getEventDescription(metric, player) {
    const playerName = player?.name || player?.full_name || player?.username || 'Player';
    return `${playerName} – ${metric.label}`;
  }

  /** Subclasses may override to add sport-specific validation logic. */
  canRecordMetric(_metricKey, _player, _currentScore) {
    return { allowed: true };
  }
}

export default BaseSportScoring;
